import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import BookingHeader from '@features/booking/components/BookingHeader';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { confirmReservation } from '../api/reservations';
import type { ConfirmReservationResponse } from '../api/reservations';

function formatDate(resDate: string): string {
  try {
    const datePart = resDate.split(' ')[0];
    const date = new Date(datePart + 'T12:00:00');
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return resDate;
  }
}

function formatTime(resTime: string): string {
  try {
    const parts = resTime.split(' ');
    const timePart = parts.length > 1 ? parts[1] : parts[0];
    const [h, m] = timePart.split(':');
    return `${h}:${m}`;
  } catch {
    return resTime;
  }
}

const ConfirmReservationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const calledRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<ConfirmReservationResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id || calledRef.current) return;
    calledRef.current = true;

    confirmReservation(id)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [id]);

  const displayDate = data?.resDate?.date ? formatDate(data.resDate.date) : '';
  const displayTime = data?.resTime?.date ? formatTime(data.resTime.date) : '';
  const isSuccess = data?.status === 'success';
  const isInvalidRes = data?.status === 'invalid_res' || data?.status === 'res_not_found';

  const renderContent = () => {
    if (!id || error) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">
              {t('CONFIRM_RESERVATION_ERROR_TITLE')}
            </h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">
              {t('CONFIRM_RESERVATION_ERROR_SUBTITLE')}
            </p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('CONFIRM_RESERVATION_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (isLoading) {
      return (
        <div className="text-center text-foreground/40 text-sm py-12">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />
          {t('CONFIRM_RESERVATION_LOADING')}
        </div>
      );
    }

    if (isInvalidRes) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">
              {t('CONFIRM_RESERVATION_INVALID_TITLE')}
            </h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">
              {t('CONFIRM_RESERVATION_INVALID_SUBTITLE')}
            </p>
            {data?.contact && (
              <a href={`mailto:${data.contact}`} className="text-brand underline text-sm mb-4">
                {data.contact}
              </a>
            )}
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('CONFIRM_RESERVATION_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (isSuccess) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">
              {t('CONFIRM_RESERVATION_SUCCESS_TITLE')}
            </h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">
              {t('CONFIRM_RESERVATION_SUCCESS_SUBTITLE')}
            </p>
          </div>

          <div className="w-full max-w-sm px-4 space-y-4">
            <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
              {data?.name && (
                <SummaryRow label={t('CONFIRM_RESERVATION_GUEST')} value={data.name} />
              )}
              {displayDate && (
                <SummaryRow label={t('CONFIRM_RESERVATION_DATE')} value={displayDate} />
              )}
              {displayTime && (
                <SummaryRow label={t('CONFIRM_RESERVATION_TIME')} value={displayTime} />
              )}
              {data?.partySize && (
                <SummaryRow label={t('CONFIRM_RESERVATION_GUESTS')} value={data.partySize} />
              )}
              {data?.serviceType && (
                <SummaryRow label={t('CONFIRM_RESERVATION_SERVICE')} value={data.serviceType} />
              )}
            </div>
          </div>

          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('CONFIRM_RESERVATION_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {renderContent()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ConfirmReservationPage;
